import { useEffect, useRef, useState } from "react";
import { Luggage, Plus, Trash2, Pencil, Camera, Tag, AlertTriangle, Lock, Upload, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { useTravelers } from "./useTravelers";

/* Baggage vault — photo of each bag + checked-in tag, and lost-bag reports. */
const inp = "border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary bg-white";
const OWNERS = [["self", "My bag"], ["spouse", "Spouse's bag"], ["child", "Child's bag"], ["traveler", "Family member's bag"], ["other", "Other"]];
const OWNER_LABEL = Object.fromEntries(OWNERS);
const RESPONSES = [["", "No response yet"], ["found_at_baggage_area", "Found — waiting at the baggage area"],
  ["deliver_to_hotel_or_home", "Will find and deliver to my hotel / home"], ["still_searching", "Still searching"]];
const RESPONSE_LABEL = Object.fromEntries(RESPONSES);
const empty = { name: "", owner: "self", owner_name: "", description: "" };

export default function BaggageManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const travelers = useTravelers(open);

  const load = () => api.get("/bags").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Bag name is required"); return; }
    try {
      await api.post("/bags", form);
      toast.success(form.id ? "Bag updated" : "Bag added — now upload a photo of it");
      setForm(empty); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save bag"); }
  };

  const remove = async (id) => { await api.delete(`/bags/${id}`); toast.success("Bag removed"); load(); };

  return (
    <div data-testid="baggage-manager" className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-900 text-[#EA4335] text-2xl flex items-center gap-2"><Luggage className="w-6 h-6" /> My Baggage</h2>
        <button onClick={() => { setForm(empty); setOpen((o) => !o); }} data-testid="baggage-new"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700"><Plus className="w-4 h-4" /> Add bag</button>
      </div>
      <div className="border border-orange-300 bg-orange-50/60 p-3 mb-4 text-sm text-orange-900 flex items-start gap-2" data-testid="baggage-tip">
        <Lightbulb className="w-4 h-4 mt-0.5 shrink-0 text-orange-500" />
        <span><b>It is always safe to take a picture of each bag and upload to My Travel Hub!</b> Add the checked-in baggage tag photo too — if a bag goes missing, you'll have everything the airline asks for.</span>
      </div>
      <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Private to you.</p>

      {open && (
        <div className="border border-orange-300 bg-white p-4 mb-4" data-testid="baggage-form">
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bag name (e.g. Blue Samsonite spinner) *" data-testid="baggage-name" className={inp} />
            <select value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} data-testid="baggage-owner" className={inp}>
              {OWNERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {form.owner === "child" && (
              <input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Child's name" data-testid="baggage-owner-name" className={inp} />
            )}
            {form.owner === "traveler" && (
              travelers.length > 0 ? (
                <select value={form.owner_name} onChange={(e) => { const t = travelers.find((x) => x.name === e.target.value); setForm({ ...form, owner_name: e.target.value, owner_id: t?.id || null }); }} data-testid="baggage-owner-traveler" className={inp}>
                  <option value="">Pick a saved traveller…</option>
                  {travelers.map((t) => <option key={t.id} value={t.name}>{t.name}{t.relation ? ` (${t.relation})` : ""}</option>)}
                </select>
              ) : (
                <input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Family member's name" data-testid="baggage-owner-name" className={inp} />
              )
            )}
            {form.owner === "other" && (
              <input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Whose bag? (specify)" data-testid="baggage-owner-name" className={inp} />
            )}
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (colour, size, stickers…)" data-testid="baggage-description" className={`${inp} sm:col-span-2`} />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
            <button onClick={save} data-testid="baggage-save" className="px-4 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700">{form.id ? "Update" : "Save"} bag</button>
          </div>
        </div>
      )}

      {items.length === 0 && !open ? (
        <p className="text-sm text-muted-foreground">No bags yet. Add each bag you travel with, snap a photo and keep the baggage tag — priceless if one goes missing.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((it) => (
            <BagCard key={it.id} it={it} onEdit={() => { setForm({ ...empty, id: it.id, name: it.name, owner: it.owner, owner_name: it.owner_name || "", description: it.description || "" }); setOpen(true); }}
              onRemove={() => remove(it.id)} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoSlot({ it, kind, label, icon }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [bump, setBump] = useState(0);
  const token = localStorage.getItem("token");
  const url = it[`${kind}_photo_url`] ? `${API}/bags/${it.id}/photo/${kind}?auth=${token}&v=${bump}` : null;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/bags/${it.id}/photo/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success(`${label} uploaded`);
      it[`${kind}_photo_url`] = `/api/bags/${it.id}/photo/${kind}`;
      setBump((b) => b + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  return (
    <div className="flex-1 min-w-0">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" data-testid={`baggage-${kind}-img-${it.id}`}>
          <img src={url} alt={label} className="w-full h-24 object-cover border border-zinc-200 rounded" />
        </a>
      ) : (
        <div className="w-full h-24 border border-dashed border-zinc-300 rounded flex items-center justify-center text-muted-foreground">{icon}</div>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`baggage-${kind}-file-${it.id}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`baggage-${kind}-upload-${it.id}`}
        className="mt-1.5 w-full inline-flex items-center justify-center gap-1.5 border border-zinc-300 px-2 py-1.5 text-xs hover:border-primary disabled:opacity-50">
        {busy ? <Upload className="w-3.5 h-3.5 animate-pulse" /> : <Camera className="w-3.5 h-3.5" />} {url ? "Replace" : "Upload"}
      </button>
    </div>
  );
}

function BagCard({ it, onEdit, onRemove, onChange }) {
  const [lostOpen, setLostOpen] = useState(false);
  const [lf, setLf] = useState({ lost_tag_number: it.lost_tag_number || "", lost_airline: it.lost_airline || "",
    lost_phone: it.lost_phone || "", lost_email: it.lost_email || "", reported: !!it.reported,
    airline_response: it.airline_response || "", notes: it.notes || "" });

  const ownerLabel = it.owner === "child" ? `Child${it.owner_name ? ` (${it.owner_name})` : ""}'s bag`
    : it.owner === "traveler" ? `${it.owner_name || "Family member"}'s bag`
    : it.owner === "other" ? (it.owner_name || "Other") : OWNER_LABEL[it.owner];

  const saveLost = async (lost) => {
    try {
      await api.post("/bags", { id: it.id, name: it.name, owner: it.owner, owner_name: it.owner_name,
        description: it.description, lost, ...lf });
      toast.success(lost ? "Lost-bag report saved" : "Marked as found — glad it's back!");
      setLostOpen(false); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  return (
    <div className={`border bg-white p-4 ${it.lost ? "border-rose-300" : "border-zinc-300"}`} data-testid={`baggage-card-${it.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading font-900 text-lg truncate">{it.name}</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            <span className="text-[11px] font-600 bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded" data-testid={`baggage-owner-pill-${it.id}`}>{ownerLabel}</span>
            {it.lost && <span className="inline-flex items-center gap-1 text-[11px] font-700 bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded" data-testid={`baggage-lost-badge-${it.id}`}><AlertTriangle className="w-3 h-3" /> Lost{it.airline_response ? ` — ${RESPONSE_LABEL[it.airline_response]}` : ""}</span>}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit} data-testid={`baggage-edit-${it.id}`} className="text-muted-foreground hover:text-primary p-1"><Pencil className="w-4 h-4" /></button>
          <button onClick={onRemove} data-testid={`baggage-remove-${it.id}`} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {it.description && <p className="text-xs text-muted-foreground mt-1">{it.description}</p>}

      <div className="flex gap-3 mt-3">
        <PhotoSlot it={it} kind="bag" label="Bag photo" icon={<Luggage className="w-6 h-6" />} />
        <PhotoSlot it={it} kind="tag" label="Checked-in baggage tag" icon={<Tag className="w-6 h-6" />} />
      </div>

      {it.lost && !lostOpen && (
        <div className="mt-3 border border-rose-200 bg-rose-50/50 p-2.5 text-xs space-y-0.5" data-testid={`baggage-lost-summary-${it.id}`}>
          {it.lost_tag_number && <div><span className="text-muted-foreground">Tag #: </span><span className="font-mono font-600">{it.lost_tag_number}</span></div>}
          {it.lost_airline && <div><span className="text-muted-foreground">Airline: </span><b>{it.lost_airline}</b></div>}
          {(it.lost_phone || it.lost_email) && <div><span className="text-muted-foreground">Contact: </span>{[it.lost_phone, it.lost_email].filter(Boolean).join(" · ")}</div>}
          <div><span className="text-muted-foreground">Reported to airline: </span><b>{it.reported ? "Yes" : "No"}</b>{it.reported && it.airline_response && <> — {RESPONSE_LABEL[it.airline_response]}</>}</div>
          {it.notes && <div className="text-muted-foreground whitespace-pre-line">{it.notes}</div>}
        </div>
      )}

      {lostOpen && (
        <div className="mt-3 border border-rose-300 bg-rose-50/40 p-3" data-testid={`baggage-lost-form-${it.id}`}>
          <div className="text-xs font-700 text-rose-800 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Lost-bag report</div>
          <div className="grid grid-cols-2 gap-2">
            <input value={lf.lost_tag_number} onChange={(e) => setLf({ ...lf, lost_tag_number: e.target.value })} placeholder="Baggage tag number" data-testid={`baggage-lost-tag-${it.id}`} className={inp} />
            <input value={lf.lost_airline} onChange={(e) => setLf({ ...lf, lost_airline: e.target.value })} placeholder="Airline name" data-testid={`baggage-lost-airline-${it.id}`} className={inp} />
            <input value={lf.lost_phone} onChange={(e) => setLf({ ...lf, lost_phone: e.target.value })} placeholder="Contact phone" data-testid={`baggage-lost-phone-${it.id}`} className={inp} />
            <input value={lf.lost_email} onChange={(e) => setLf({ ...lf, lost_email: e.target.value })} placeholder="Contact email" data-testid={`baggage-lost-email-${it.id}`} className={inp} />
            <label className="col-span-2 inline-flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`baggage-lost-reported-${it.id}`}>
              <input type="checkbox" checked={lf.reported} onChange={(e) => setLf({ ...lf, reported: e.target.checked })} /> Reported to the airline
            </label>
            {lf.reported && (
              <select value={lf.airline_response} onChange={(e) => setLf({ ...lf, airline_response: e.target.value })} data-testid={`baggage-lost-response-${it.id}`} className={`${inp} col-span-2`}>
                {RESPONSES.map(([v, l]) => <option key={v} value={v}>Airline response: {l}</option>)}
              </select>
            )}
            <textarea value={lf.notes} onChange={(e) => setLf({ ...lf, notes: e.target.value })} placeholder="Notes (reference number, where last seen…)" data-testid={`baggage-lost-notes-${it.id}`} rows={2} className={`${inp} col-span-2 resize-y`} />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setLostOpen(false)} className="px-3 py-1.5 border border-zinc-300 text-xs">Cancel</button>
            <button onClick={() => saveLost(true)} data-testid={`baggage-lost-save-${it.id}`} className="px-3 py-1.5 bg-rose-600 text-white text-xs font-600 hover:bg-rose-700">Save report</button>
          </div>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        {!it.lost ? (
          <button onClick={() => setLostOpen((o) => !o)} data-testid={`baggage-mark-lost-${it.id}`}
            className="inline-flex items-center gap-1.5 border border-rose-300 text-rose-700 px-2.5 py-1.5 text-xs hover:bg-rose-50">
            <AlertTriangle className="w-3.5 h-3.5" /> Lost this bag?
          </button>
        ) : (
          <>
            <button onClick={() => setLostOpen((o) => !o)} data-testid={`baggage-lost-update-${it.id}`}
              className="inline-flex items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs hover:border-rose-400">
              <Pencil className="w-3.5 h-3.5" /> Update report
            </button>
            <button onClick={() => saveLost(false)} data-testid={`baggage-found-${it.id}`}
              className="inline-flex items-center gap-1.5 border border-emerald-300 text-emerald-700 px-2.5 py-1.5 text-xs hover:bg-emerald-50">
              Bag found
            </button>
          </>
        )}
      </div>
    </div>
  );
}
